import { useEffect, useState } from 'react';
import { Form, Input, Button, Typography, message, Row, Col } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../../../store/hooks/hooks';
import {
  postLogin,
  resetPassword,
  userPasswordCreate,
} from '../../../store/slice/userSlice';
import { ReactComponent as User } from '../../../res/svg/user.svg';
import {
  strongRule,
  passwordRule,
  helperValidateInput,
  helperLowerAndUpper,
  helperSpecialCharacters,
} from '../../../utils/FormHelpers';
import {
  passwordRegexCharacters,
  passwordRegexLowUpp,
  passwordRegexEspecial,
  setAccessArray,
  getDecodedAccessToken,
} from '../../../utils/functions';
import './CommonForm.less';
import AuthModal from '../AuthModal/AuthModal';

/**
 * @component CommonForm.tsx
 * @description form to login and forgot password
 * @return { Component }
 */
interface CommonFormProps {
  isRegister: boolean;
  forgotPassword: boolean;
  successMessage: () => void;
}
function CommonForm(props: CommonFormProps) {
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { Title, Text } = Typography;
  const [form] = Form.useForm();
  const [, forceUpdate] = useState({});
  const [passText, setPassText] = useState('');
  const [passRepeat, setPassRepeat] = useState('');
  const [validPassword, setValidPassword] = useState(false);
  const [validateError, setValidateError] = useState(false);
  const [validateInput, setValidateInput] = useState(false);
  const [validateLowerAndUpper, setValidateLowerAndUpper] = useState(false);
  const [validateEspecial, setValidateEspecial] = useState(false);
  const [isForgotPasswordModal, setIsForgotPasswordModal] = useState(false);
  const [tokenInfo, setTokenInfo] = useState('');
  let navigate = useNavigate();
  const placeHolder = !props.isRegister ? 'Usuario' : 'Ingresa tu contraseña';
  const onFinish = (loginParameters: any) => {
    if (!props.forgotPassword) {
      if (!props.isRegister) {
        dispatch(postLogin(loginParameters)).then((value: any) => {
          if (value.payload.status === 200) {
            const accessArray = setAccessArray(
              {
                accesoAdministracion: value.payload.data.accesoAdministracion,
                accesoCatastro: value.payload.data.accesoCatastro,
                accesoGestion: value.payload.data.accesoGestion,
                accesoRegistros: value.payload.data.accesoRegistros,
              },
              false,
            );
            localStorage.setItem('accessArray', JSON.stringify(accessArray));
            navigate('/dashboard');
          } else if(value.payload.status !== 500){
            message.error(
              'Verifica que estés ingresando tu usuario y contraseña correctamente, si el problema persiste contacta a tu Administrador.',
            );
          } else {
            //Si el status es 500 el servidor esta fuera de servicio
            message.error(
              'El servidor se encuentra fuera de servicio',
            );
          }
        });
      } else {
        dispatch(
          userPasswordCreate({
            token: searchParams.get('token')!,
            password: loginParameters.passWordRepeat,
          }),
        ).then((value: any) => {
          if (value.payload.status) {
            message.success('Contraseña creada correctamente.');
            navigate('/login');
          } else {
            //Invalid Token y JWT expired
            if(value.payload.data.statusCode === 403){
              message.error('Tu invitación ha expirado, contacta a tu administrador');
            } else{
              message.error(value.payload.data.message);
            }
          }
        });
      }
    } else {
      dispatch(
        resetPassword({
          token: searchParams.get('token')!,
          password: loginParameters.passWordRepeat,
        }),
      ).then((value: any) => {
        if (value.payload.status) {
          props.successMessage();
        } else {
          message.error(value.payload.data.message);
        }
      });
    }
  };
  const onFinishFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo);
  };
  const showForgotPasswordModal = () => {
    setIsForgotPasswordModal(true);
  };
  // To disable submit button at the beginning.
  useEffect(() => {
    forceUpdate({});
    if (searchParams.get('token')) {
      setTokenInfo(getDecodedAccessToken(searchParams.get('token')!).email); // decode token
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const passwordCheck = (value: string) => {
    const regexCharacters = passwordRegexCharacters(value);
    const regexLowUpp = passwordRegexLowUpp(value);
    const regexEspecial = passwordRegexEspecial(value);
    setPassText(value);
    setValidateInput(regexCharacters);
    setValidateLowerAndUpper(regexLowUpp);
    setValidateEspecial(regexEspecial);
    if (!regexCharacters || !regexLowUpp || !regexEspecial) {
      setValidateError(true);
    } else {
      setValidateError(false);
    }
    setValidPassword(value !== passRepeat ? true : false);
  };
  const passwordCheckError = (value: string) => {
    const validPass = value === passText ? false : true;
    setPassRepeat(value);
    setValidPassword(validPass);
  };
  const passwordValid =
    (validPassword && passText) || (validPassword && passText !== '')
      ? 'error'
      : undefined; //valid password for password repeat
  const screenValid = props.isRegister ? passwordValid : undefined; //if it's register or login
  const passwordMessage = props.forgotPassword
    ? 'Crea tu contraseña nueva'
    : 'Hola!';
  const titleMessage = !props.isRegister ? 'Hola!' : passwordMessage;
  const errorClass = validateError
    ? 'ml-form-common__helper__text'
    : 'ml-form-common__success__text';
  const closeModal = () => {
    setIsForgotPasswordModal(false);
  };

  return (
    <div
      className={`ml-form-common-container${
        !props.isRegister ? '' : ' register'
      }`}
    >
      <Title className="ml-form-common-title">{titleMessage}</Title>
      {!props.forgotPassword && (
        <div>
        <Text type="secondary">
          {!props.isRegister
            ? 'Por favor ingresa tus datos para iniciar sesión'
            : 'Crea una contraseña para terminar de configurar tu cuenta. Con tu correo'}
        </Text>
        {
          props.isRegister ? <Text type="secondary" strong>&nbsp;{tokenInfo}</Text> : null
        }
        </div>

      )}
      <Form
        form={form}
        name={!props.isRegister ? 'loginForm' : 'registerForm'}
        initialValues={{ remember: true }}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        autoComplete="off"
        className="ml-form-common-form"
      >
        <Form.Item
          name={!props.isRegister ? 'username' : 'password'}
          hasFeedback
          validateTrigger={'onBlur'}
          style={{paddingBottom: '8px'}}
          rules={
            !props.isRegister
              ? [
                  {
                    type: 'email',
                    message: 'Ingresa un correo electrónico válido.',
                  },
                  { required: true, message: 'Ingresa tu correo electrónico.' },
                ]
              : [...passwordRule, ...strongRule]
          }
        >
          {!props.isRegister ? (
            <Input
              suffix={<User />}
              className="ml-form-common-user-input"
              placeholder={placeHolder}
            />
          ) : (
            <Input.Password
              className="ml-form-common-password-input"
              placeholder={'Ingresa tu contraseña'}
              onChange={(e) => passwordCheck(e.target.value)}
            />
          )}
        </Form.Item>
        {props.isRegister && (
          <div className="ml-form-common-validation-container">
            <div className="ml-form-common-validation-item">
              {helperValidateInput(validateInput, validateError)}
              <Text disabled className={!validateInput ? errorClass : ''}>
                Mínimo 8 caracteres con letras y números.
              </Text>
            </div>
            <div className="ml-form-common-validation-item">
              {helperLowerAndUpper(validateLowerAndUpper, validateError)}
              <Text
                disabled
                className={!validateLowerAndUpper ? errorClass : ''}
              >
                Mínimo 1 letra mayúscula y 1 minúscula.
              </Text>
            </div>
            <div className="ml-form-common-validation-item">
              {helperSpecialCharacters(validateEspecial, validateError)}
              <Text disabled className={!validateEspecial ? errorClass : ''}>
                Mínimo 1 caracter especial como !@#$%&*)(+*.
              </Text>
            </div>
          </div>
        )}
        <Form.Item
          hasFeedback
          validateStatus={screenValid}
          name={!props.isRegister ? 'password' : 'passWordRepeat'}
          /*Se incluye las reglas del input password junto con otra que compara 
            que los dos inputs tengan los mismos valores
          */
          rules={[...passwordRule, ...strongRule, ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('password') === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error('Las dos contraseñas tienen que ser iguales'));
            },
          }),]}
        >
          <Input.Password
            className="ml-form-common-password-input"
            placeholder={
              !props.isRegister ? 'Password' : 'Confirma tu contraseña'
            }
            onChange={(e) => passwordCheckError(e.target.value)}
          />
        </Form.Item>
        {!props.isRegister && (
          <Text
            className="ml-form-common-link-label"
            onClick={showForgotPasswordModal}
          >
            Restablecer contraseña
          </Text>
        )}
        <Form.Item shouldUpdate>
          {() => (
            <Button
              className="ml-form-common-button"
              type="primary"
              htmlType="submit"
              shape="round"
              size={'large'}
              disabled={
                !form.isFieldsTouched(true) ||
                !!form.getFieldsError().filter(({ errors }) => errors.length)
                  .length
              }
            >
              {!props.isRegister ? 'Ingresar' : 'Crear contraseña'}
            </Button>
          )}
        </Form.Item>
      </Form>
      <Row>
        <Col span={24}>
          <AuthModal
            buttonText="Crear usuario"
            user={{}}
            isModalVisible={isForgotPasswordModal}
            closeModal={closeModal}
          />
        </Col>
      </Row>
    </div>
  );
}

export default CommonForm;
